/**
 * The HTTP client.
 *
 * Thin, because TanStack Query owns caching and request state. What lives here
 * is the part Query cannot do: attaching the access token, and transparently
 * renewing it when it expires.
 */

/**
 * A path, not an origin.
 *
 * The SPA and the API share an origin - Vite proxies `/api` to nginx in
 * development, nginx serves both in production - so this is relative in both.
 * That is also what makes the refresh cookie work at all: a cookie set on one
 * origin is not sent to another.
 */
const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** The shape `App\Support\ErrorHandler` writes for every failure. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/**
 * A failed request, carrying the server's machine-readable code.
 *
 * Callers switch on `code`, never on `message` - the message is written for a
 * person and is expected to be reworded. `fieldErrors` is pulled out because
 * form validation is the one detail shape common enough to deserve a named
 * property rather than a cast at every call site.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get fieldErrors(): Record<string, string> {
    const fields = this.details.fields;

    return typeof fields === 'object' && fields !== null ? (fields as Record<string, string>) : {};
  }
}

/* ------------------------------------------------------------------------- */
/* Session wiring                                                             */
/* ------------------------------------------------------------------------- */

/**
 * What the session store hands this module at load.
 *
 * Callbacks rather than an import, because the store needs this module to make
 * its own requests and importing it back would be a cycle. The dependency runs
 * one way: the store depends on the client, the client is handed what it needs.
 */
interface SessionBridge {
  getAccessToken: () => string | null;

  /**
   * Exchange the refresh cookie for a new access token. True if it worked.
   *
   * **Must be single-flight.** Refresh tokens rotate, so two concurrent calls
   * mean the second presents a token the first already consumed - which the
   * server correctly reads as theft and answers by revoking the entire family
   * (HANDOFF §7.1). A client that races itself signs its own user out.
   *
   * The guarantee lives in `renewSession` in the session store rather than
   * here, because the other entry point - restoring the session on page load -
   * has to share the same guard, and one wrapped around this would not cover it.
   */
  refresh: () => Promise<boolean>;

  /** Tear down local state; the cookie is already dead. */
  onSessionLost: () => void;
}

let session: SessionBridge = {
  getAccessToken: () => null,
  refresh: async () => false,
  onSessionLost: () => {},
};

export function configureApi(bridge: SessionBridge): void {
  session = bridge;
}

/* ------------------------------------------------------------------------- */
/* Requests                                                                   */
/* ------------------------------------------------------------------------- */

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Query parameters. Undefined and null are dropped rather than sent as "undefined". */
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Skip the token and the refresh-on-401 retry.
   *
   * Set for the auth endpoints themselves. Refreshing in response to a failed
   * login is nonsense, and retrying `/auth/refresh` when it 401s is a loop.
   */
  anonymous?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    /*
     * One retry, and only one.
     *
     * A 401 here means the access token expired - which happens every fifteen
     * minutes and should be invisible. If the renewal works the original
     * request is replayed with the new token; if it does not, the session is
     * genuinely over.
     */
    const renewed = await session.refresh();

    if (renewed) {
      return unwrap<T>(await send(path, options));
    }

    session.onSessionLost();
  }

  return unwrap<T>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { body, query, headers, anonymous, ...rest } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  const token = anonymous ? null : session.getAccessToken();

  return fetch(url, {
    ...rest,
    /*
     * The refresh token is an httpOnly cookie (§7.1), so it only rides along if
     * credentials are included. Without this, a page reload silently fails to
     * restore the session and looks exactly like a logout bug.
     */
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function unwrap<T>(response: Response): Promise<T> {
  // 204 and friends have no body; parsing one throws on an empty string.
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();

  let parsed: unknown = null;

  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    /*
     * Not JSON. In practice this is nginx's own error page - a 502 while the
     * API container restarts, or a 503 - and the raw HTML is useless to a
     * caller. Turned into a normal ApiError so every failure has one shape.
     */
    throw new ApiError(response.status, 'unexpected_response', 'The server returned something unexpected.');
  }

  if (!response.ok) {
    const envelope = parsed as Partial<ApiErrorBody> | null;
    const error = envelope?.error;

    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? response.statusText,
      error?.details ?? {},
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  // Used where the request genuinely replaces a whole resource - an album's
  // running order is the list, not an edit to it.
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};
